import CompartilharVaga from "../components/CompartilharVaga";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import Header from "../components/Header";
import CadastroEmpresaModal from "../components/CadastroEmpresaModal";
import BuscaCandidatos from "../components/BuscaCandidatos";
import FiltrosColapsaveis, { campoFiltro, CampoRotulado } from "../components/FiltrosColapsaveis";
import ModalCandidato from "../components/ModalCandidato";
import { HtmlEditor, WhatsappEditor } from "../components/RichEditors";
import { NovaVagaForm } from "./NovaVagaPage";
import { useAuth } from "../context/AuthContext";
import { listarVagas, vagasRecomendadas, minhasVagas, candidatosDaVaga, abordarCandidatos, modelosEncerramento, criarModeloEncerramento, TAGS_ENCERRAMENTO, minhasCandidaturas, empresaLogoUrl, AREAS_ATUACAO, SENIORIDADES, caminhoVaga, listarEmpresasNomes, obterMeuPerfil } from "../services/api";
import { teclaAtiva } from "../utils/teclaAtiva";
import { rotulo, rotuloLista } from "../utils/rotulos";
import { noSiteDaEmpresa } from "../utils/candidatura";
import { reputacaoDaEmpresa, reputacaoEmLinha } from "../utils/reputacao";

// Selinho "dá retorno" do card. Sky, a mesma cor do bloco "Índice de retorno" da página da empresa: é o mesmo
// número, então tem de parecer a mesma coisa. Discreto de propósito: informa, não vira propaganda da empresa.
const TITULO_DA_RETORNO = "Pelo menos 60% de quem se candidatou a esta empresa pelo eaiRH disse ter recebido retorno (com 5 respostas ou mais).";

function corMatch(m) { return m >= 70 ? "bg-green-100 text-green-800" : m >= 40 ? "bg-yellow-100 text-yellow-800" : "bg-gray-100 text-gray-600"; }

export default function VagasPage() {
  const { user, isEmpresa, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Vagas abertas da própria empresa — usadas na aba "Buscar candidatos" para começar PELA VAGA.
  const [minhasAbertas, setMinhasAbertas] = useState([]);
  // ?vaga= e ?candidatos= vêm do resumo diário à empresa: a aba já abre na vaga do e-mail e a lista fica
  // restrita a quem veio nele. Só dígitos entram — o valor vai direto para a URL da API.
  const vagaDaUrl = /^\d+$/.test(searchParams.get("vaga") || "") ? searchParams.get("vaga") : "";
  const idsDoResumo = (searchParams.get("candidatos") || "").split(",").filter((s) => /^\d+$/.test(s)).slice(0, 200).join(",");
  const [vagaSel, setVagaSel] = useState(vagaDaUrl);   // vaga escolhida no combobox da aba "Buscar candidatos"
  const ehCandidato = user?.tipo === 0;
  // Perfil do candidato: só para decidir o nudge "matches baixos". Sem ele o nudge mandava ao currículo quem JÁ
  // tinha currículo completo (RH/recrutador casa pouco com o mural) — Raissa e Giovanna, 14/09/2026, rodaram
  // /curriculo ↔ /vagas várias vezes. undefined = carregando (não mostra nada até saber).
  const [perfilCand, setPerfilCand] = useState(undefined);
  useEffect(() => {
    if (!ehCandidato) return;
    obterMeuPerfil().then(setPerfilCand).catch(() => setPerfilCand(null));
  }, [ehCandidato]);
  // A MESMA régua do CandidaturaNoSite (VagaPage): experiência, skill ou PDF = tem currículo.
  const candTemCurriculo = !!perfilCand && ((perfilCand.experiencias?.length || 0) > 0 || (perfilCand.skills?.length || 0) > 0 || !!perfilCand.temCurriculo);
  const podePublicar = isEmpresa || isAdmin; // empresa (e admin operador) publica vagas
  const podeBuscarCand = isEmpresa || isAdmin; // empresa verificada (e admin) enxerga a aba "Buscar candidatos"
  // Link do e-mail (?buscar=1&vaga=…&candidatos=…) aberto sem ninguém logado: a aba nem existiria e a pessoa
  // cairia no mural. Passa pelo login e volta para o mesmo endereço. O usuário vem do storage na hora (sem
  // espera), então isto não empurra para o login quem já está logado.
  useEffect(() => {
    if (searchParams.get("buscar") != null && !user)
      navigate(`/entrar?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`, { replace: true });
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // 🔎 "Só no cargo": casa o termo em TÍTULO + SKILLS, ignorando a descrição.
  //
  // Nasceu em 08/09/2026 de um post que não deu para fazer. Os posts por setor usam link de mural
  // filtrado e funcionam (`?q=recepcionista` devolveu 10 recepcionistas em 10). Para o grupo de
  // automação não deu: `?q=rpa` abre com "Cientista de Dados" e "Comprador Pleno", porque RPA é
  // FERRAMENTA e aparece na descrição de qualquer vaga que liste a stack de passagem — enquanto
  // "recepcionista" é CARGO e mora no título de quem é aquilo.
  // Varrer a descrição é o que salva a busca por cidade e sinônimo; é também o que estraga a busca
  // por ferramenta. Por isso virou ESCOLHA, e não troca de regra: o padrão não mudou para ninguém.
  // ?q=devops,cloud → o mural JÁ ABRE FILTRADO por essas palavras.
  //
  // Existe por causa da divulgação: um post que anuncia "6 vagas de DevOps" e manda para o mural inteiro
  // obriga quem clicou a procurar entre 88 vagas o que acabou de ler. Esse atrito é onde a pessoa desiste.
  // Com o filtro na URL, um link só resolve — e sem espalhar um link por vaga no post, que é o que o
  // LinkedIn penaliza.
  const tagsDaUrl = (searchParams.get("q") || "")
    .split(",").map((s) => s.trim()).filter(Boolean).slice(0, 6);
  // ?empresa=Nestle,Wipro → mesma lógica do ?q=, pra post/link poder abrir já filtrado por empresa(s).
  const empresasDaUrl = (searchParams.get("empresa") || "")
    .split(",").map((s) => s.trim()).filter(Boolean).slice(0, 10);

  // 🚨 O `q` PRECISA NASCER AQUI, E NÃO SÓ NOS CHIPS (corrigido 18/09/2026).
  //
  // `empresa` e `escopo` sempre leram a URL; o `q` nascia "" e era lido só dez linhas abaixo, para
  // desenhar os CHIPS. Mas chip é estado separado (`tags`) — quem viaja para o servidor é `filtros`.
  // Resultado: `/vagas?q=telecom` desenhava o chip "telecom" na tela, chamava a API SEM o q e mostrava
  // "7.628 vagas encontradas". O filtro existia aos olhos da pessoa e não existia no resultado.
  //
  // Quem mais sofria era justamente quem o `?q=` foi criado para servir: todo link de post ("6 vagas de
  // DevOps") abria o mural inteiro, que é o atrito que ele veio eliminar.
  // 🔗 CIDADE, REMOTO E CONTRATAÇÃO TAMBÉM NASCEM DA URL (26/09/2026).
  //
  // Mesma lição do `q` em 18/09, repetida em outro campo: o assistente perguntava cidade e modalidade,
  // montava o link e a pessoa caía num mural sem nenhum dos dois — *"a página de vagas não abriu
  // filtrada com o que solicitei a ele"*. Quem viaja para o servidor é `filtros`; parâmetro que não
  // nasce aqui simplesmente não existe, por mais que apareça na barra de endereço.
  const [filtros, setFiltros] = useState({ area: "", senioridade: "", matchMin: "", titulo: "", candidatura: "",
    cidade: searchParams.get("cidade") || "",
    remoto: searchParams.get("remoto") === "true" ? "true" : "",
    contratacao: searchParams.get("contratacao") || "",
    q: tagsDaUrl.join(","),
    empresa: empresasDaUrl.join(","),
    escopo: searchParams.get("escopo") === "titulo" ? "titulo" : "",
    // ?retorno=1 → já abre em "Só empresas que dão retorno" (link de post: "vagas de quem responde").
    retorno: searchParams.get("retorno") === "1" ? "1" : "" });

  const [tags, setTags] = useState(tagsDaUrl);   // palavras-chave (chips) da busca de vagas
  const [tagInput, setTagInput] = useState("");

  // Filtro de empresa: combobox de CHIPS (várias empresas de uma vez, é OR no servidor — "Nestle" ou
  // "Wipro" — igual à leitura natural de "vagas dessas duas empresas"). Diferente das palavras-chave
  // (texto livre, AND): aqui só entra empresa que já existe no mural, sugerida pelo autocomplete —
  // digitar errado e a pessoa concluir "essa empresa não tem vaga" seria o mesmo defeito que a cidade
  // sem acento já causou noutro filtro.
  const [empresasLista, setEmpresasLista] = useState([]);
  const [empresasSel, setEmpresasSel] = useState(empresasDaUrl);
  const [empresaInput, setEmpresaInput] = useState("");
  const [empresaAberto, setEmpresaAberto] = useState(false);
  const [empresaAtivo, setEmpresaAtivo] = useState(-1);
  // O que está ESCRITO no campo Cidade. Fica separado de `filtros.cidade` (o que foi para o servidor) pelo
  // mesmo motivo do `empresaInput`: o texto muda a cada tecla, a busca só depois da pausa (ver o debounce abaixo).
  const [cidadeTexto, setCidadeTexto] = useState(searchParams.get("cidade") || "");
  useEffect(() => { listarEmpresasNomes({ comVagas: true }).then(setEmpresasLista).catch(() => {}); }, []);
  const querBuscar = searchParams.get("buscar") != null; // ?buscar=1 abre direto a aba de busca de candidatos
  // A busca (quando o link pede) tem prioridade p/ quem pode vê-la — senão cai no default por tipo.
  // A EMPRESA cai nas próprias vagas, não no mural inteiro: ela entra aqui para cuidar do que publicou.
  // O mural de todas as vagas é vitrine de candidato — para ela era uma aba a mais para ignorar toda vez.
  const [view, setView] = useState(
    (querBuscar && (isEmpresa || isAdmin)) ? "buscar"
      // Link com ?q= vai SEMPRE para o mural completo: quem clicou num post de "vagas de DevOps" quer ver
      // aquelas vagas, não as recomendadas para o perfil dele — e a empresa não quer só as próprias.
      : tagsDaUrl.length > 0 ? "todas"
      // ?view=minhas vem do item "Minhas candidaturas" do menu.
      : (ehCandidato ? (searchParams.get("view") === "minhas" ? "minhas" : "rec") : (isEmpresa ? "minhas" : "todas"))
  );
  const [rows, setRows] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [cadEmpresa, setCadEmpresa] = useState(false); // popup de cadastro de empresa
  const [modalNova, setModalNova] = useState(false);   // modal de publicar vaga (sem sair da página)
  // Paginação do mural, feita NO SERVIDOR desde 06/09/2026: a tela pede só a página que vai mostrar.
  // Ordem da lista. Match é o padrão porque responde "o que serve para mim"; mas quem volta ao site todo dia
  // quer saber o que ENTROU, e nessa pergunta a lista por match é a mesma de ontem — a vaga nova nasce no meio
  // dela e passa despercebida.
  const [ordem, setOrdem] = useState("match");
  const [porPagina, setPorPagina] = useState(25);
  const [pagina, setPagina] = useState(1);
  // Total FILTRADO que o servidor informou. `null` = esta lista nao veio paginada do servidor
  // (recomendadas e "minhas vagas" chegam inteiras e continuam sendo fatiadas aqui).
  const [totalServidor, setTotalServidor] = useState(null);

  const reqSeq = useRef(0); // guard contra resposta fora de ordem (trocar view/filtro rápido pode deixar uma carga lenta sobrescrever)

  const carregar = useCallback(async (f, v, pag = 1, pp = porPagina) => {
    const seq = ++reqSeq.current;
    setCarregando(true); setErro(null);
    try {
      const fil = { ...f, remoto: f.remoto === "" ? undefined : f.remoto };
      let novasRows;
      if (ehCandidato && v === "minhas") {
        if (seq === reqSeq.current) setTotalServidor(null);
        const cc = await minhasCandidaturas();
        // Carrega o retorno junto da vaga: é o dado que a pessoa vem aqui procurar.
        novasRows = cc.map((c) => ({ ...c.vaga, match: null, retornoEm: c.retornoEm, retornoForaDaPlataforma: c.retornoForaDaPlataforma }));
      } else if (ehCandidato && v !== "todas") {
        if (seq === reqSeq.current) setTotalServidor(null);
        const rec = await vagasRecomendadas({ ...fil, ordem });
        novasRows = rec.map((r) => ({ ...r.vaga, match: r.match, motivos: r.motivos }));
      } else if (v === "minhas") {
        // minhasVagas() não recebe filtro — e a barra de filtros ficava desenhada na tela sem fazer nada, o que
        // é pior que não ter filtro: a empresa marca "Remota", o total não muda e ela conclui que o site está
        // quebrado. São as vagas da própria empresa (dezenas, não milhares), então filtrar aqui é instantâneo.
        if (seq === reqSeq.current) setTotalServidor(null);
        const mv = await minhasVagas();
        const txt = (x) => (x || "").toLowerCase();
        novasRows = mv
          .filter((x) => !f.titulo || txt(x.titulo).includes(txt(f.titulo)))
          .filter((x) => !f.area || txt(x.areaAtuacao).includes(txt(f.area)))
          .filter((x) => !f.senioridade || txt(x.senioridade) === txt(f.senioridade))
          .filter((x) => !f.cidade || txt(x.cidade).includes(txt(f.cidade)))
          .filter((x) => f.remoto !== "true" || x.remoto)
          .filter((x) => !f.q || f.q.split(",").map((t) => t.trim()).filter(Boolean).every((t) =>
            [x.titulo, x.descricao, x.cidade, x.areaAtuacao, x.senioridade, (x.skills || []).join(" ")]
              .some((campo) => txt(campo).includes(txt(t)))))
          .map((x) => ({ ...x, match: null }));
      } else {
        // 📄 UMA PÁGINA POR VEZ, do tamanho que a pessoa escolheu em "Por página".
        //
        // Até 06/09/2026 a tela baixava o acervo inteiro (`take: 6000`, 4 MB) e recortava aqui. Com 3.000
        // vagas isso era meio minuto de tela branca no 4G — e quem procura emprego está no celular.
        // O `total` que volta é o do FILTRO, não o do acervo, e é ele que alimenta a contagem e o número
        // de páginas: pedir 25 e mostrar "3.053 encontradas" só faz sentido se o 3.053 respeitar o filtro.
        // A ORDEM VAI JUNTO. Antes o seletor existia na tela e não chegava ao servidor: mexer nele não
        // mudava nada na aba "Todas as vagas". Com a lista paginada no servidor isso ficaria permanente,
        // porque o navegador não tem mais o acervo para reordenar.
        const vs = await listarVagas({ ...fil, take: pp, skip: (pag - 1) * pp, ordem });
        // Aceita as DUAS formas de resposta de propósito. Quando a paginação foi para o servidor
        // (06/09/2026) a resposta deixou de ser uma lista e virou { total, itens } — e quem estava com a
        // página aberta na hora do deploy tinha o JS antigo chamando .map em um objeto: o mural quebrava
        // até um Ctrl+F5. Tolerar as duas formas faz a próxima mudança de formato não quebrar ninguém.
        const lista = Array.isArray(vs) ? vs : (vs?.itens || []);
        if (seq === reqSeq.current) setTotalServidor(Array.isArray(vs) ? null : (vs?.total ?? 0));
        // Sem currículo não há match, então a lista já vem por data — ordenar aqui é só para o seletor não
        // ficar decorativo em quem não está logado.
        novasRows = lista.map((x) => ({ ...x, match: null }));
      }
      if (seq === reqSeq.current) setRows(novasRows);
    } catch (e) { if (seq === reqSeq.current) setErro(e.message); }
    finally { if (seq === reqSeq.current) setCarregando(false); }
  }, [ehCandidato, ordem, porPagina]);

  // Carrega uma vez as vagas abertas da empresa (não depende do filtro do mural).
  useEffect(() => {
    if (!podeBuscarCand) return;
    minhasVagas().then((vs) => setMinhasAbertas((vs || []).filter((v) => v.status === 0 || v.status === 1)))
                 .catch(() => setMinhasAbertas([]));
  }, [podeBuscarCand]);

  useEffect(() => { carregar(filtros, view); }, [carregar]); // eslint-disable-line react-hooks/exhaustive-deps

  // Todo caminho que muda o RESULTADO volta para a página 1: a página 7 do resultado anterior não diz
  // nada sobre o novo, e com paginação de servidor pedir a página 7 de um filtro menor traria vazio.
  const aplicar = (novos) => { const f = { ...filtros, ...novos }; setFiltros(f); setPagina(1); carregar(f, view, 1); };
  const limpar = () => { const f = { area: "", cidade: "", senioridade: "", remoto: "", matchMin: "", q: "", titulo: "", candidatura: "", empresa: "", escopo: "", contratacao: "", retorno: "" }; setFiltros(f); setTags([]); setTagInput(""); setEmpresasSel([]); setEmpresaInput(""); setCidadeTexto(""); setPagina(1); carregar(f, view, 1); };

  // ⌨️ CIDADE E EMPRESA FILTRAM AO DIGITAR (03/11/2026).
  //
  // Os dois só filtravam no Enter, e no celular quase ninguém aperta Enter: a pessoa escrevia "Recife",
  // olhava a mesma lista de antes e concluía que o filtro não funciona (ou que não há vaga lá). Agora a busca
  // sai sozinha 400 ms depois da última tecla: uma ida ao servidor por PAUSA, nunca uma por tecla.
  //
  // Na empresa, o texto ainda não escolhido entra junto dos chips (é OR no servidor, igual aos chips): quem
  // escreve "Nest" já vê a Nestlé. Escolher a sugestão aplica na hora (addEmpresa) e o efeito abaixo vê que
  // não há nada novo a mandar, então não sai uma segunda requisição.
  //
  // As refs existem porque o temporizador dispara 400 ms depois: com o `aplicar` daquela renderização, um
  // filtro mudado nesse meio-tempo (área, senioridade) seria desfeito pelo `filtros` velho que ele carrega.
  const filtrosRef = useRef(filtros); filtrosRef.current = filtros;
  const aplicarRef = useRef(aplicar); aplicarRef.current = aplicar;
  useEffect(() => {
    const cidade = cidadeTexto.trim();
    const empresa = [...empresasSel, empresaInput.trim()].filter(Boolean).join(",");
    const atual = filtrosRef.current;
    if (cidade === (atual.cidade || "") && empresa === (atual.empresa || "")) return;
    const t = setTimeout(() => aplicarRef.current({ cidade, empresa }), 400);
    return () => clearTimeout(t);
  }, [cidadeTexto, empresaInput, empresasSel]);
  const trocarView = (v) => { setView(v); setPagina(1); carregar(filtros, v, 1); };
  // Já em /vagas, o item do menu só troca a URL — o useState acima não roda de novo.
  useEffect(() => {
    if (ehCandidato && searchParams.get("view") === "minhas" && view !== "minhas") trocarView("minhas");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Ir para outra página é uma ida ao servidor, não um recorte do que já está na tela.
  const irParaPagina = (n) => {
    const alvo = Math.min(Math.max(1, n), totalPaginas);
    if (alvo === pagina) return;
    setPagina(alvo);
    if (totalServidor !== null) carregar(filtros, view, alvo);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Palavras-chave (tags) da busca de vagas: cada tag filtra os campos da vaga (AND), igual à busca de candidatos.
  const addTag = (raw) => {
    const t = raw.replace(/,/g, "").trim();
    if (!t || tags.some((x) => x.toLowerCase() === t.toLowerCase())) { setTagInput(""); return; }
    const novas = [...tags, t]; setTags(novas); setTagInput(""); aplicar({ q: novas.join(",") });
  };
  const removeTag = (i) => { const novas = tags.filter((_, j) => j !== i); setTags(novas); aplicar({ q: novas.join(",") }); };
  const onTagKey = (e) => {
    if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(tagInput); }
    else if (e.key === "Backspace" && !tagInput && tags.length) removeTag(tags.length - 1);
  };

  // Sugestões do combobox de empresa: filtra pelo texto digitado, tira quem já foi selecionado, teto de 8
  // (igual ao EmpresaAutocomplete) pra não virar uma lista de 500 itens na tela.
  const empresaQ = empresaInput.trim().toLowerCase();
  const empresaSugestoes = empresasLista
    .filter((e) => !empresasSel.some((s) => s.toLowerCase() === e.nome.toLowerCase()))
    .filter((e) => empresaQ.length === 0 || e.nome.toLowerCase().includes(empresaQ))
    .slice(0, 8);
  const addEmpresa = (nome) => {
    const n = (nome || "").trim();
    if (!n || empresasSel.some((x) => x.toLowerCase() === n.toLowerCase())) { setEmpresaInput(""); return; }
    // Fica ABERTO depois de escolher (não fecha): é assim que dá pra selecionar a segunda, terceira empresa
    // sem precisar clicar fora do campo e voltar — cada clique só soma mais um chip, igual multi-select.
    const novas = [...empresasSel, n]; setEmpresasSel(novas); setEmpresaInput(""); setEmpresaAberto(true); setEmpresaAtivo(-1);
    aplicar({ empresa: novas.join(",") });
  };
  const removeEmpresa = (i) => {
    // O texto ainda no campo vai junto (mesma regra do debounce): sem ele, o efeito mandaria uma 2ª busca 400 ms depois.
    const novas = empresasSel.filter((_, j) => j !== i); setEmpresasSel(novas);
    aplicar({ empresa: [...novas, empresaInput.trim()].filter(Boolean).join(",") });
  };
  const onEmpresaKey = (e) => {
    if (!empresaAberto || empresaSugestoes.length === 0) {
      if (e.key === "Backspace" && !empresaInput && empresasSel.length) removeEmpresa(empresasSel.length - 1);
      return;
    }
    if (e.key === "ArrowDown") { e.preventDefault(); setEmpresaAtivo((i) => Math.min(i + 1, empresaSugestoes.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setEmpresaAtivo((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); addEmpresa(empresaSugestoes[Math.max(empresaAtivo, 0)]?.nome); }
    else if (e.key === "Escape") { setEmpresaAberto(false); setEmpresaAtivo(-1); }
    else if (e.key === "Backspace" && !empresaInput && empresasSel.length) removeEmpresa(empresasSel.length - 1);
  };

  const selCls = "min-w-0 flex-1 sm:flex-none rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm outline-none focus:ring-2 focus:ring-primary";

  // Recorte da página atual. `pagina` é corrigida por efeito (e não aqui) para não renderizar vazio no meio do
  // caminho: filtrar de 40 para 8 vagas estando na página 3 deixaria a lista em branco com resultados existindo.
  const paginadoNoServidor = totalServidor !== null;
  const total = paginadoNoServidor ? totalServidor : rows.length;
  const totalPaginas = Math.max(1, Math.ceil(total / porPagina));
  const inicio = (pagina - 1) * porPagina;
  const fim = Math.min(inicio + porPagina, total);
  // Paginado no servidor, `rows` JÁ é a página pedida — fatiar de novo cortaria o que acabou de chegar.
  const visiveis = paginadoNoServidor ? rows : rows.slice(inicio, inicio + porPagina);
  // JANELA de páginas. Com 3.000 vagas de 25 em 25 são 123 páginas, e desenhar os 123 botões enchia a
  // tela de números que ninguém clica. Mostra o começo, o fim e a vizinhança de onde a pessoa está.
  const paginas = (() => {
    if (totalPaginas <= 7) return Array.from({ length: totalPaginas }, (_, i) => i + 1);
    const perto = [pagina - 1, pagina, pagina + 1].filter((n) => n > 1 && n < totalPaginas);
    const ns = [1, ...perto, totalPaginas];
    const saida = [];
    ns.forEach((n, i) => { if (i && n - ns[i - 1] > 1) saida.push("…"); saida.push(n); });
    return saida;
  })();

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-6xl mx-auto px-4 md:px-6 py-8 pb-28 md:pb-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-text">Vagas</h1>
            <p className="text-gray-600 text-sm mt-1">
              {ehCandidato
                ? (ordem === "recentes"
                    ? "Das mais recentes para as mais antigas — com a reputação da empresa ao lado."
                    : ordem === "retorno"
                    ? "Primeiro as empresas que mais dão retorno a quem se candidata, com a reputação ao lado."
                    : "Ordenadas pelo match com o seu currículo — com a reputação da empresa ao lado.")
                : "Vagas abertas com a reputação da empresa."}
            </p>
          </div>
          {podePublicar && (
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setModalNova(true)} className="rounded-xl bg-orange-600 hover:bg-orange-700 text-white font-semibold text-sm px-4 py-2">+ Publicar vaga</button>
            </div>
          )}
        </div>

        {/* CTA dupla (visitante deslogado): ativa os dois lados do mural — candidato monta currículo p/ match,
            empresa publica vaga. Ataca o gap "abriu /vagas e não se cadastrou". */}
        {!user && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4">
              <h3 className="font-bold text-gray-900">👤 É candidato?</h3>
              <p className="text-sm text-gray-600 mt-1">Monte seu currículo (grátis) e a gente cruza com as vagas por <strong>match</strong>. Quanto mais completo, melhores as recomendações.</p>
              {/* O botão promete "montar currículo" e, sem redirect, o cadastro largava a pessoa na HOME — em 10/09
                  uma candidata veio do LinkedIn, clicou aqui, criou a conta, confirmou o e-mail e caiu em "/". */}
              <Link to={`/entrar?cadastro=1&redirect=${encodeURIComponent("/curriculo?voltar=/vagas")}`} className="inline-block mt-3 rounded-xl bg-primary hover:bg-brand text-white font-semibold px-4 py-2 text-sm">Criar conta e montar currículo</Link>
            </div>
            <div className="rounded-2xl border border-orange-200 bg-orange-50 p-4">
              <h3 className="font-bold text-gray-900">🏢 É empresa ou recrutador?</h3>
              <p className="text-sm text-gray-600 mt-1">Publique sua vaga <strong>grátis</strong> e veja os candidatos mais compatíveis. Dar retorno constrói a sua reputação no eaiRH.</p>
              <button onClick={() => setCadEmpresa(true)} className="inline-block mt-3 rounded-xl bg-orange-600 hover:bg-orange-700 text-white font-semibold px-4 py-2 text-sm">Cadastrar empresa e publicar</button>
            </div>
          </div>
        )}

        {/* Abas: Recomendadas/Minhas candidaturas (candidato) | Todas/Minhas vagas (empresa) */}
        {user && (
          <div className="mt-4 flex flex-wrap gap-2">
            {ehCandidato ? (
              <>
                <TabBtn active={view === "rec"} onClick={() => trocarView("rec")}>Recomendadas para mim</TabBtn>
                {/* O candidato logado só via as RECOMENDADAS — o mural inteiro sumia justamente para quem
                    tem currículo. Ficou visível quando a senioridade passou a eliminar match de dois degraus:
                    quem é sênior deixou de enxergar as vagas júnior, inclusive para saber que existem.
                    Recomendação é atalho, não cerca: quem quer olhar tudo tem que conseguir. */}
                <TabBtn active={view === "todas"} onClick={() => trocarView("todas")}>Todas as vagas</TabBtn>
                <TabBtn active={view === "minhas"} onClick={() => trocarView("minhas")}>Minhas candidaturas</TabBtn>
              </>
            ) : (
              <>
                {/* "Todas as vagas" é vitrine de candidato. Para a empresa era aba a ignorar toda visita — e o
                    mural continua acessível pelo site público, sem precisar ocupar espaço no trabalho dela. */}
                {!isEmpresa && <TabBtn active={view === "todas"} onClick={() => trocarView("todas")}>Todas as vagas</TabBtn>}
                <TabBtn active={view === "minhas"} onClick={() => trocarView("minhas")}>Minhas vagas publicadas</TabBtn>
              </>
            )}
            {podeBuscarCand && <TabBtn active={view === "buscar"} onClick={() => trocarView("buscar")}>🔎 Buscar candidatos</TabBtn>}
          </div>
        )}

        {view === "buscar" ? (
          <div className="mt-5">
            {/* COMEÇAR PELA VAGA, em um combobox: a empresa escolhe a vaga e os candidatos aparecem logo
                abaixo, sem trocar de tela. Sem vaga escolhida, cai na busca livre do banco de talentos. */}
            {minhasAbertas.length > 0 && (
              <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm mb-5">
                <label className="block text-sm font-semibold text-gray-700 mb-1">Candidatos para a vaga</label>
                <select value={vagaSel} onChange={(e) => setVagaSel(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary">
                  <option value="">— Busca livre no banco de talentos —</option>
                  {minhasAbertas.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.titulo}{v.cidade ? ` · ${v.cidade}` : (v.remoto ? " · Remoto" : "")}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1.5">Escolha a vaga e veja quem combina, ordenado por match — sem precisar montar filtro.</p>
              </div>
            )}

            {/* key: trocar de vaga no combobox recria a lista (e descarta o recorte do e-mail, que só vale
                para a vaga que veio no link). */}
            {vagaSel ? <CandidatosDaVaga key={vagaSel} vagaId={Number(vagaSel)} vaga={minhasAbertas.find((v) => String(v.id) === String(vagaSel))}
              ids={String(vagaSel) === vagaDaUrl ? idsDoResumo : ""} /> : (
            <BuscaCandidatos />
            )}
          </div>
        ) : (
        <>
        {/* Filtros (candidato e empresa) */}
        <div className="mt-5">
        <FiltrosColapsaveis onLimpar={limpar} resumo={[
          filtros.titulo && `título: ${filtros.titulo}`,
          filtros.empresa && `empresa: ${filtros.empresa}`,
          filtros.area && filtros.area,
          filtros.senioridade && filtros.senioridade,
          filtros.cidade && filtros.cidade,
          filtros.remoto === "true" && "remota",
          filtros.candidatura === "eairh" && "candidatura pelo eaiRH",
          filtros.candidatura === "site" && "candidatura no site da empresa",
          filtros.matchMin && `match ≥ ${filtros.matchMin}%`,
          filtros.escopo === "titulo" && "só no cargo",
          filtros.retorno === "1" && "só empresas que dão retorno",
        ]}>
          {/* Busca por pedaço do título: com dezenas de vagas publicadas, achar "a do SAP" pelos selects é
              trabalho — e o campo de palavras-chave do mural não aparece em "minhas vagas". */}
          {view === "minhas" && (
            <input className={campoFiltro} aria-label="Buscar por parte do título da vaga" placeholder="Buscar no título da vaga"
              value={filtros.titulo} onChange={(e) => aplicar({ titulo: e.target.value })} />
          )}
          <select className={campoFiltro} aria-label="Filtrar por área" value={filtros.area} onChange={(e) => aplicar({ area: e.target.value })}>
            <option value="">Área (todas)</option>{AREAS_ATUACAO.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <select className={campoFiltro} aria-label="Filtrar por senioridade" value={filtros.senioridade} onChange={(e) => aplicar({ senioridade: e.target.value })}>
            <option value="">Senioridade (todas)</option>{SENIORIDADES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          {/* 📄 CONTRATAÇÃO (26/09/2026, pedido do Nelson). Só mostra vaga que DECLARA o regime: 80% do
              mural não declara, e incluir o "não informado" em CLT seria o portal afirmando um regime que
              a empresa não disse — o candidato descobriria na entrevista. Filtro menor e verdadeiro. */}
          <select className={campoFiltro} aria-label="Filtrar por tipo de contratação" value={filtros.contratacao}
                  onChange={(e) => aplicar({ contratacao: e.target.value })}>
            <option value="">Contratação (todas)</option>
            <option value="CLT">CLT</option>
            <option value="PJ">PJ</option>
            <option value="Estagio">Estágio</option>
            <option value="Temporario">Temporário</option>
            <option value="Trainee">Trainee</option>
          </select>
          {/* Filtra sozinho depois da pausa na digitação (debounce lá em cima); o Enter só adianta a busca. */}
          <input className={campoFiltro} placeholder="Cidade" aria-label="Filtrar por cidade" value={cidadeTexto}
            onChange={(e) => setCidadeTexto(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && aplicar({ cidade: cidadeTexto.trim() })} />
          <div className="relative">
            <div className={`${campoFiltro} flex flex-wrap items-center gap-1 h-auto min-h-[38px] py-1`}>
              {empresasSel.map((nome, i) => (
                <span key={i} className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary text-xs font-semibold px-2 py-0.5">
                  {nome}<button type="button" onClick={() => removeEmpresa(i)} aria-label={`Remover ${nome}`} className="hover:text-red-600 leading-none">×</button>
                </span>
              ))}
              <input className="flex-1 min-w-[80px] text-sm outline-none"
                aria-label="Filtrar por empresa" placeholder={empresasSel.length ? "+ empresa" : "Empresa"}
                value={empresaInput}
                onChange={(e) => { setEmpresaInput(e.target.value); setEmpresaAberto(true); setEmpresaAtivo(-1); }}
                onFocus={() => setEmpresaAberto(true)}
                onKeyDown={onEmpresaKey}
                onBlur={() => setTimeout(() => setEmpresaAberto(false), 150)} />
            </div>
            {empresaAberto && empresaSugestoes.length > 0 && (
              <ul className="absolute z-10 mt-1 w-full max-h-56 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                {empresaSugestoes.map((e, i) => (
                  <li key={e.id ?? e.nome}>
                    <button type="button"
                      className={`block w-full text-left px-3 py-1.5 text-sm ${i === empresaAtivo ? "bg-primary/10 text-primary" : "hover:bg-gray-50"}`}
                      onMouseDown={(ev) => ev.preventDefault()}
                      onClick={() => addEmpresa(e.nome)}>
                      {e.nome}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input type="checkbox" checked={filtros.remoto === "true"} onChange={(e) => aplicar({ remoto: e.target.checked ? "true" : "" })} /> Somente remotas
          </label>

          {/* 🔎 "Só no cargo": manda o servidor casar o termo em TÍTULO + SKILLS, ignorando a descrição.
              Existe porque FERRAMENTA nao se busca como CARGO: "recepcionista" mora no titulo de quem e
              recepcionista, mas "RPA" e "Selenium" aparecem na descricao de qualquer vaga que liste a
              stack de passagem — e assim `?q=rpa` abria com "Cientista de Dados" e "Comprador Pleno".
              Fica desligado por padrao: varrer a descricao e o que salva a busca por cidade e sinonimo. */}
          <label className="flex items-center gap-2 text-sm text-gray-600" title="Ignora a descrição da vaga: útil para buscar ferramenta (RPA, Selenium) sem trazer quem só cita a tecnologia de passagem.">
            <input type="checkbox" checked={filtros.escopo === "titulo"}
              onChange={(e) => aplicar({ escopo: e.target.checked ? "titulo" : "" })} /> Só no cargo
          </label>

          {ehCandidato && (
            <CampoRotulado rotulo="Match mín." sufixo="%">
              <input type="number" min="0" max="100" step="5" className={campoFiltro} aria-label="Match mínimo (%)"
                value={filtros.matchMin} onChange={(e) => setFiltros({ ...filtros, matchMin: e.target.value })} onKeyDown={(e) => e.key === "Enter" && aplicar({})} />
            </CampoRotulado>
          )}
        </FiltrosColapsaveis>
        </div>

        {/* Palavras-chave (tags) — busca por termos no título, descrição, skills, área, senioridade, cidade e empresa da vaga. */}
        {(view === "rec" || view === "todas") && (
          <div className="mt-3 rounded-2xl border border-gray-200 bg-white p-3 shadow-sm">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-sm font-semibold text-gray-600 mr-1">Palavras-chave:</span>
              {tags.map((t, i) => (
                <span key={i} className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary text-xs font-semibold px-2 py-0.5">
                  {t}<button type="button" onClick={() => removeTag(i)} aria-label={`Remover ${t}`} className="hover:text-red-600 leading-none">×</button>
                </span>
              ))}
              <input className="flex-1 min-w-0 sm:min-w-[160px] text-sm outline-none py-1"
                placeholder={tags.length ? "+ palavra-chave" : "ex.: react, remoto, home office — Enter para adicionar"}
                value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={onTagKey} onBlur={() => addTag(tagInput)} />
            </div>
          </div>
        )}

        {/* Nudge de onboarding: candidato com perfil esparso vê só matches fracos (ex.: 20% por ser remota). Sem isto,
            ele não percebe que completar o currículo melhora o match — vazamento no funil. Só aparece quando TODOS os
            matches visíveis são fracos (< 40%), o que sinaliza perfil incompleto. */}
        {/* 🚨 Só para quem NÃO tem currículo: com currículo completo o match baixo é do mural, não do perfil, e
            mandar "complete seu currículo" virava laço (link sem ?voltar, e o currículo devolve para /vagas). */}
        {ehCandidato && perfilCand !== undefined && !candTemCurriculo && view === "rec" && !carregando && !erro && rows.length > 0 && rows.every((v) => (v.match ?? 0) < 40) && (
          <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            💡 Seus matches estão baixos. <Link to="/curriculo" className="font-semibold underline">Complete seu currículo</Link> (área, cidade, senioridade e skills) para encontrar vagas mais alinhadas ao seu perfil.
          </div>
        )}

        {/* TOTAL + QUANTAS POR PÁGINA. Sem o total, "22 resultados" e "2 resultados" davam a mesma sensação de
            lista curta — quem filtra precisa saber se sobrou pouco por causa do filtro ou porque o mural é pequeno. */}
        {!carregando && !erro && total > 0 && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-sm">
            <p className="text-gray-600">
              <b className="text-gray-900">{total.toLocaleString("pt-BR")}</b> {total === 1 ? "vaga encontrada" : "vagas encontradas"}
              {total > porPagina && <span className="text-gray-400"> · mostrando {inicio + 1}–{fim}</span>}
            </p>
            {/* SELETOR DE ORDEM. Fica junto do "por página" porque as duas coisas respondem à mesma pergunta —
                como eu quero ver esta lista — e porque ali é onde o olho vai depois de ler quantas vagas há.
                Só aparece para quem tem currículo: sem match, "mais compatíveis" não significa nada. */}
            <label className="flex items-center gap-2 text-gray-600">
              Ordenar por
              <select value={ordem} onChange={(e) => { setOrdem(e.target.value); setPagina(1); }}
                aria-label="Ordenar as vagas"
                className="rounded-lg border border-gray-300 px-2.5 py-1.5 outline-none focus:ring-2 focus:ring-primary">
                {/* "Mais compatíveis" só aparece onde o match existe (aba Recomendadas, que compara com o
                    currículo). Na aba "Todas as vagas" ela ficava na tela sem ordenar nada — o servidor
                    nunca recebia a escolha. O padrão de lá é a variedade: uma vaga de cada empresa por
                    rodada, para uma carga em massa não tomar a primeira página. */}
                {ehCandidato && view !== "todas"
                  ? <option value="match">Mais compatíveis</option>
                  : <option value="match">Variado (uma por empresa)</option>}
                <option value="recentes">Mais recentes</option>
                {/* Sobe quem tem o maior índice de retorno (o mesmo da página da empresa); o resto vem depois,
                    na ordem de sempre. Não esconde vaga nenhuma: para isso existe a caixa ao lado. */}
                {(view === "rec" || view === "todas") && <option value="retorno">Empresas que mais respondem</option>}
              </select>
            </label>
            {/* 🙋 SÓ EMPRESAS QUE DÃO RETORNO (03/11/2026). Fica aqui fora, ao lado do "Ordenar por", e não no
                "Filtros" recolhido: é a pergunta que traz a pessoa ao eaiRH, e filtro que ninguém acha não existe.
                O critério é o do servidor (pelo menos 5 pessoas responderam e 60% ou mais tiveram retorno). */}
            {(view === "rec" || view === "todas") && (
              <label className="flex items-center gap-2 text-gray-600"
                title="Empresas em que pelo menos 60% de quem se candidatou pelo eaiRH disse ter recebido retorno (com 5 respostas ou mais).">
                <input type="checkbox" checked={filtros.retorno === "1"}
                  onChange={(e) => aplicar({ retorno: e.target.checked ? "1" : "" })} />
                Só empresas que dão retorno
              </label>
            )}
            {/* TIPO DE CANDIDATURA — FORA do "Filtros" recolhido, de propósito.
                Ele estava lá dentro junto com área e porte, e ninguém achava. Mas é a informação que mais
                muda o esforço de quem procura: pelo eaiRH é um clique e o processo fica acompanhado; no
                site da empresa exige preencher o formulário dela. Decisão do Nelson em 06/09/2026, quando
                o mural passou de 3.000 vagas e dois terços viraram handoff.
                Nasce em "Ambas" — filtrar por padrão esconderia vaga sem a pessoa ter pedido. */}
            <label className="flex items-center gap-2 text-gray-600">
              Candidatura
              <select value={filtros.candidatura} onChange={(e) => aplicar({ candidatura: e.target.value })}
                aria-label="Filtrar por onde a candidatura acontece"
                className="rounded-lg border border-gray-300 px-2.5 py-1.5 outline-none focus:ring-2 focus:ring-primary">
                <option value="">Ambas</option>
                <option value="eairh">Pelo eaiRH</option>
                <option value="site">No site da empresa</option>
              </select>
            </label>
            <label className="flex items-center gap-2 text-gray-600">
              Por página
              {/* Mudar o tamanho da página muda o PEDIDO ao servidor (o efeito abaixo recarrega, porque
                  `carregar` depende de porPagina), não o recorte do que já está na tela. */}
              <select value={porPagina} onChange={(e) => { setPorPagina(Number(e.target.value)); setPagina(1); }}
                aria-label="Vagas por página"
                className="rounded-lg border border-gray-300 px-2.5 py-1.5 outline-none focus:ring-2 focus:ring-primary">
                {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
          </div>
        )}

        {/* CELULAR: cartões. A tabela de 9 colunas obrigava a rolar de lado e escondia local, senioridade e
            match — o candidato decidia sem os dados que fazem decidir. No desktop a tabela continua. */}
        <div className="mt-4 space-y-2 md:hidden">
          {carregando && <p className="text-gray-400 text-sm px-1">Carregando...</p>}
          {!carregando && visiveis.map((v) => (
            <div key={v.id} onClick={() => navigate(caminhoVaga(v))} {...teclaAtiva(() => navigate(caminhoVaga(v)), null)}
              className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm active:bg-gray-50 cursor-pointer">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-bold text-gray-900 break-words">{v.titulo}</div>
                  <div className="text-sm text-gray-600 mt-0.5">
                    {v.empresa}{v.empresaVerificada && <span className="text-blue-600"> ✔</span>}
                  </div>
                </div>
                {ehCandidato && v.match != null && (
                  <span className={`shrink-0 font-bold rounded-full px-2.5 py-1 text-xs ${corMatch(v.match)}`}>{v.match}%</span>
                )}
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
                <span className="rounded-full bg-gray-100 text-gray-700 px-2 py-0.5">{v.remoto ? "Remota" : (v.cidade || "—")}</span>
                {/* Onde a candidatura acontece — dito ANTES do clique, nao depois. */}
                {noSiteDaEmpresa(v)
                  ? <span className="rounded-full bg-orange-50 text-orange-800 px-2 py-0.5 font-semibold" title="Você é levado ao site da empresa para se candidatar. O eaiRH acompanha o processo pelo que você contar.">no site da empresa</span>
                  : <span className="rounded-full bg-emerald-50 text-emerald-800 px-2 py-0.5 font-semibold" title="A candidatura sai pelo eaiRH e o retorno é acompanhado automaticamente.">candidatura pelo eaiRH</span>}
                {/* A pessoa abre esta tela para saber de UMA coisa: teve retorno ou não. */}
                {view === "minhas" && ehCandidato && (v.retornoEm
                  ? <span className="rounded-full bg-green-100 text-green-800 px-2 py-0.5 font-semibold">✓ a empresa respondeu em {new Date(v.retornoEm).toLocaleDateString("pt-BR")}</span>
                  : <span className="rounded-full bg-amber-50 text-amber-800 px-2 py-0.5 font-semibold">aguardando retorno</span>)}
                {v.senioridade && <span className="rounded-full bg-gray-100 text-gray-700 px-2 py-0.5">{v.senioridade}</span>}
                {(() => {
                  const r = reputacaoEmLinha(v.empresaScore, v.empresaSelo, v.empresaClassificacao);
                  return <span className={`rounded-full px-2 py-0.5 font-semibold ${r.cor}`} title={r.titulo}>{r.texto}</span>;
                })()}
                {/* Mesmo critério do filtro "Só empresas que dão retorno": quem vem do servidor é o carimbo. */}
                {v.empresaDaRetorno && <span className="rounded-full bg-sky-50 text-sky-800 px-2 py-0.5 font-semibold" title={TITULO_DA_RETORNO}>dá retorno</span>}
                {/* Celular: mesmo dado da coluna do desktop, como chip — a empresa também tria pelo telefone. */}
                {v.candidaturas != null && (
                  <span className={`rounded-full px-2 py-0.5 font-semibold ${v.candidaturas > 0 ? "bg-primary/10 text-primary" : "bg-gray-100 text-gray-500"}`}>
                    {v.candidaturas > 0 ? `${v.candidaturas} candidatura${v.candidaturas > 1 ? "s" : ""}` : "sem candidaturas"}
                  </span>
                )}
              </div>
              {v.skills?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {v.skills.slice(0, 4).map((s) => (
                    <span key={s} className="rounded-full bg-blue-50 text-blue-800 px-2 py-0.5 text-[11px] font-semibold">{s}</span>
                  ))}
                  {v.skills.length > 4 && <span className="text-[11px] text-gray-400 self-center">+{v.skills.length - 4}</span>}
                </div>
              )}
              <div className="mt-3 flex items-center justify-between">
                <span className="text-primary text-sm font-semibold">Ver vaga →</span>
                <CompartilharVaga vaga={v} compacto />
              </div>
            </div>
          ))}
          {!carregando && total === 0 && (
            <p className="text-center text-gray-500 py-8">
              Nenhuma vaga encontrada com esses filtros.
              {/* A caixa "Só empresas que dão retorno" some junto com a barra quando o total é zero: sem este
                  botão, a pessoa ficaria presa num filtro que ela não vê mais na tela. */}
              {filtros.retorno === "1" && <>{" "}<button type="button" onClick={() => aplicar({ retorno: "" })} className="text-primary font-semibold underline">Ver vagas de todas as empresas</button></>}
            </p>
          )}
        </div>

        {/* Grid de vagas (desktop) */}
        <div className="mt-4 hidden md:block overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr>
                {ehCandidato && <th className="text-left px-4 py-3">Match</th>}
                <th className="text-left px-4 py-3">Vaga</th>
                <th className="text-left px-4 py-3">Empresa</th>
                <th className="text-left px-4 py-3">Reputação</th>
                <th className="text-left px-4 py-3">Porte</th>
                <th className="text-left px-4 py-3">Local</th>
                <th className="text-left px-4 py-3">Senioridade</th>
                {/* ONDE A CANDIDATURA ACONTECE — dito ANTES do clique.
                    Estava só no chip do celular e no filtro recolhido; na tabela do desktop, que é onde a
                    maioria olha, não aparecia em lugar nenhum. A diferença muda o esforço de quem procura:
                    pelo eaiRH é um clique e o processo fica acompanhado; no site da empresa exige
                    preencher o formulário dela. */}
                <th className="text-left px-4 py-3">Candidatura</th>
                <th className="text-left px-4 py-3">Aberta há</th>
                {view === "minhas" && !ehCandidato && <th className="text-left px-4 py-3">Candidaturas</th>}
                <th className="px-4 py-3"><span className="sr-only">Compartilhar</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {carregando && <tr><td colSpan="10" className="px-4 py-6 text-gray-400">Carregando...</td></tr>}
              {!carregando && erro && <tr><td colSpan="10" className="px-4 py-6 text-red-600">{erro}</td></tr>}
              {!carregando && !erro && total === 0 && (
                <tr><td colSpan="10" className="px-4 py-8 text-center text-gray-500">
                  {/* 🚨 "MINHAS CANDIDATURAS" VAZIA não é falta de match. Dizia "Complete seu currículo" também aqui, e a
                      Raissa (14/09/2026, currículo completo) rodou /curriculo › /vagas 3 vezes procurando o que faltava. */}
                  {filtros.retorno === "1" && view !== "minhas"
                    ? <>Ainda não há vagas de empresas com histórico de retorno nesses filtros. O grupo cresce conforme quem se candidata pelo eaiRH conta se recebeu resposta.{" "}
                        <button type="button" onClick={() => aplicar({ retorno: "" })} className="text-primary font-semibold underline">Ver vagas de todas as empresas</button></>
                    : ehCandidato && view === "minhas"
                    ? <>Você ainda não tem candidaturas. Abra uma vaga que te interessa e use o botão de candidatura dela; depois ela aparece aqui.{" "}
                        <button type="button" onClick={() => trocarView("rec")} className="text-primary font-semibold underline">Ver vagas para você</button></>
                    : ehCandidato
                    ? <>Nenhuma vaga deu match ainda. <Link to="/curriculo" className="text-primary font-semibold underline">Complete seu currículo</Link> (área, cidade, senioridade, skills) para encontrar vagas alinhadas ao seu perfil.</>
                    : <>🚀 Ainda não há vagas publicadas — estamos começando.<br />É empresa ou recrutador? <Link to={user ? "/vagas/nova" : "/entrar"} className="text-primary font-semibold underline">Publique a primeira vaga</Link> — é grátis e a IA preenche pra você a partir do texto.</>}
                </td></tr>
              )}
              {!carregando && !erro && visiveis.map((v) => (
                <tr key={v.id} className="hover:bg-gray-50 cursor-pointer focus:outline-none focus:bg-blue-50" onClick={() => navigate(caminhoVaga(v))}
                    {...teclaAtiva(() => navigate(caminhoVaga(v)), null)} aria-label={`Abrir vaga ${v.titulo}`}>
                  {ehCandidato && <td className="px-4 py-3">{v.match != null ? <span className={`font-bold rounded-full px-2.5 py-1 text-xs ${corMatch(v.match)}`}>{v.match}%</span> : <span className="text-gray-300">—</span>}</td>}
                  <td className="px-4 py-3">
                    <div className="font-semibold text-gray-900">{v.titulo}{v.remoto && <span className="ml-2 text-[11px] text-green-700">remota</span>}</div>
                    {/* Skills na vitrine: é com elas que a pessoa decide se a vaga é dela SEM precisar abrir. */}
                    {v.skills?.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {v.skills.slice(0, 4).map((s) => (
                          <span key={s} className="rounded-full bg-blue-50 text-blue-800 px-2 py-0.5 text-[11px] font-semibold">{s}</span>
                        ))}
                        {v.skills.length > 4 && <span className="text-[11px] text-gray-400 self-center">+{v.skills.length - 4}</span>}
                      </div>
                    )}
                    {/* O prazo aparece ANTES do clique, mas SÓ quando a empresa o declarou: vaga espelhada
                        nasce com o padrão de 15 dias sem ninguém de lá ter concordado. */}
                    {v.prazoDeclaradoPelaEmpresa && v.respostaPrazoDias > 0 && (
                      <div className="mt-1 text-[11px] font-semibold text-emerald-700">
                        ⏱️ retorno prometido em até {v.respostaPrazoDias} dias
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1.5">
                      {v.empresaTemLogo && <img src={empresaLogoUrl(v.empresaId)} alt="" loading="lazy" className="h-5 w-5 rounded object-contain bg-white border border-gray-100" />}
                      {v.empresa}{v.empresaVerificada && <span className="text-blue-600">✔</span>}
                    </span>
                    {v.empresaDaRetorno && (
                      <div className="mt-0.5"><span className="rounded-full bg-sky-50 text-sky-800 px-1.5 py-0.5 text-[10px] font-semibold" title={TITULO_DA_RETORNO}>dá retorno</span></div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {(() => {
                      const r = reputacaoDaEmpresa(v.empresaScore, v.empresaSelo, v.empresaClassificacao);
                      return (
                        <span className="inline-flex flex-col items-start gap-0.5" title={r.titulo}>
                          {r.nota && <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${r.cor}`}>{r.nota}</span>}
                          <span className={`text-[11px] leading-tight ${r.nota ? "text-gray-500" : "text-gray-400 italic"}`}>{r.rotulo}</span>
                        </span>
                      );
                    })()}
                    {v.empresaAlertas?.length > 0 && <span className="ml-1" title={v.empresaAlertas.join(", ")}>{v.empresaAlertas[0].split(" ")[0]}</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{v.empresaPorte || "—"}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {v.remoto ? "Remota" : (v.cidade || "—")}
                    {view === "minhas" && ehCandidato && (v.retornoEm
                      ? <div className="text-[11px] font-semibold text-green-700">✓ respondida em {new Date(v.retornoEm).toLocaleDateString("pt-BR")}</div>
                      : <div className="text-[11px] font-semibold text-amber-700">aguardando retorno</div>)}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{v.senioridade || "—"}</td>
                  {/* A COLUNA QUE FALTAVA. Verde = a candidatura sai pelo eaiRH e o retorno e acompanhado
                      automaticamente. Laranja = a pessoa e levada ao site da empresa, com consentimento, e
                      o acompanhamento depende do que ela contar. */}
                  <td className="px-4 py-3">
                    {noSiteDaEmpresa(v)
                      ? <span className="rounded-full bg-orange-50 text-orange-800 px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap"
                              title="Você é levado ao site da empresa para se candidatar. O eaiRH acompanha o processo pelo que você contar.">no site da empresa</span>
                      : <span className="rounded-full bg-emerald-50 text-emerald-800 px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap"
                              title="A candidatura sai pelo eaiRH e o retorno é acompanhado automaticamente.">pelo eaiRH</span>}
                  </td>
                  {/* "1d" era lido como "um dia inteiro no ar". Ontem é ontem — e vaga nova é argumento
                      de candidatura, então vale escrever por extenso em vez de abreviar. */}
                  <td className="px-4 py-3 text-gray-600">
                    {v.diasEmAberto === 0 ? "hoje" : v.diasEmAberto === 1 ? "ontem" : `${v.diasEmAberto} dias`}
                  </td>
                  {/* Só a dona da vaga recebe `candidaturas` do backend (null nas demais telas) — por isso a
                      coluna aparece sozinha em "Minhas vagas publicadas" sem precisar de condicional de rota. */}
                  {v.candidaturas != null && (
                    <td className="px-4 py-3">
                      {v.candidaturas > 0
                        ? <span className="font-bold rounded-full px-2.5 py-1 text-xs bg-primary/10 text-primary">{v.candidaturas} {v.candidaturas === 1 ? "candidatura" : "candidaturas"}</span>
                        : <span className="text-xs text-gray-400">nenhuma ainda</span>}
                      {v.interesses > 0 && <span className="ml-1 text-[11px] text-gray-500">+{v.interesses} interesse{v.interesses > 1 ? "s" : ""}</span>}
                    </td>
                  )}
                  {/* Compartilhar sem precisar abrir a vaga: quem varre o mural procurando algo para um amigo
                      não deveria ter que entrar em cada uma. Ícone discreto para não poluir 21 linhas. */}
                  <td className="px-4 py-3"><CompartilharVaga vaga={v} compacto /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Navegação: só aparece quando existe mais de uma página — barra fixa num mural de 22 vagas seria ruído. */}
        {!carregando && !erro && totalPaginas > 1 && (
          <nav className="mt-4 flex flex-wrap items-center justify-center gap-2" aria-label="Paginação das vagas">
            <button type="button" disabled={pagina === 1} onClick={() => irParaPagina(pagina - 1)}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-semibold text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50">
              ← Anterior
            </button>
            {paginas.map((n, i) => (n === "…"
              ? <span key={`reticencia${i}`} className="px-1 text-sm text-gray-400">…</span>
              : <button key={n} type="button" onClick={() => irParaPagina(n)}
                  aria-current={n === pagina ? "page" : undefined}
                  className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${n === pagina ? "bg-primary text-white" : "border border-gray-300 text-gray-700 hover:bg-gray-50"}`}>
                  {n}
                </button>))}
            <button type="button" disabled={pagina === totalPaginas} onClick={() => irParaPagina(pagina + 1)}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-semibold text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50">
              Próxima →
            </button>
          </nav>
        )}
        </>
        )}
      </main>
      {modalNova && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto" onClick={() => setModalNova(false)}>
          <div className="bg-background rounded-2xl shadow-2xl max-w-3xl w-full my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 sticky top-0 bg-background rounded-t-2xl z-10">
              <h2 className="text-lg font-bold text-text">Publicar vaga</h2>
              <button onClick={() => setModalNova(false)} aria-label="Fechar" className="text-gray-400 hover:text-gray-700 text-2xl leading-none">×</button>
            </div>
            <div className="p-5">
              <NovaVagaForm compacto onPublicado={() => { setModalNova(false); trocarView("minhas"); }} />
            </div>
          </div>
        </div>
      )}
      <CadastroEmpresaModal open={cadEmpresa} onClose={() => setCadEmpresa(false)} />
    </div>
  );
}


// Abordar o candidato PARA ESTA VAGA, sem sair do modal. A mensagem já vem escrita (o recrutador só ajusta)
// porque campo em branco é o que faz a abordagem virar "oi, tudo bem?" — ou não acontecer.
// O contato do candidato não aparece: o convite sai pelo eaiRH, que é o que permite cobrar o retorno depois.
// Convite do candidato para uma vaga — MESMO formulário do retorno: e-mail com formatação, WhatsApp em texto,
// modelos da empresa, tags e escolha de canal. Reescrever a mensagem a cada pessoa é o que faz o recrutador
// desistir de convidar; o modelo é o que torna a abordagem sustentável.
function AbordarCandidato({ vaga, c, onPronto }) {
  const primeiro = (c.nome || "").split(" ")[0] || "";
  const empresaNome = vaga?.empresa || "nossa empresa";

  const resolver = (t) => (t || "")
    .replace(/\{nome\}/g, primeiro)
    .replace(/\{vaga\}/g, vaga?.titulo || "")
    .replace(/\{empresa\}/g, empresaNome);

  // 16/10/2026 (Nelson): o convite chega pelo e-mail/WhatsApp do eaiRH, então fala da empresa em TERCEIRA pessoa.
  // Antes era "Vi seu perfil ... a nossa vaga ... a gente dá retorno por aqui" — parecia o eaiRH contratando, e o
  // "por aqui" brigava com o rodapé "não responda por aqui" (1º convite real: Kelly → Felipe).
  const padraoHtml = "<p>Oi, {nome}! 👋</p><p>A <b>{empresa}</b> viu seu perfil no eaiRH e te convidou a se candidatar à vaga de <b>{vaga}</b>.</p><p>Se fizer sentido para você, é só abrir a vaga e enviar sua candidatura. A empresa dá retorno pelo eaiRH, seguindo ou não. 🙌</p>";
  const padraoWpp = "Oi, {nome}! 👋\n\nA *{empresa}* viu seu perfil no eaiRH e te convidou a se candidatar à vaga de *{vaga}*.\n\nSe fizer sentido para você, é só abrir a vaga e enviar sua candidatura. A empresa dá retorno pelo eaiRH, seguindo ou não. 🙌";

  const [msg, setMsg] = useState(resolver(padraoHtml));
  const [msgWpp, setMsgWpp] = useState(resolver(padraoWpp));
  const [rev, setRev] = useState(0);
  const [modelos, setModelos] = useState([]);
  const [porEmail, setPorEmail] = useState(true);
  const [porWhatsapp, setPorWhatsapp] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [ok, setOk] = useState(null);
  const [erro, setErro] = useState(null);

  const recarregarModelos = () => modelosEncerramento().then((r) => setModelos(r || [])).catch(() => {});
  useEffect(() => { recarregarModelos(); }, []);

  const aplicarModelo = (id) => {
    const m = modelos.find((x) => String(x.id) === String(id));
    if (!m) return;
    setMsg(resolver(m.corpo || ""));
    setMsgWpp(resolver(m.corpoWhatsapp || (m.corpo || "").replace(/<br\s*\/?>/gi, "\n").replace(/<\/p>/gi, "\n\n").replace(/<[^>]+>/g, "")));
    setRev((r) => r + 1);
  };

  // Volta as tags no lugar dos dados desta pessoa — senão o modelo nasceria com o nome de um candidato dentro.
  const salvarComoModelo = async () => {
    if (!msg.trim()) { setErro("Escreva a mensagem antes de salvar como modelo."); return; }
    const nome = window.prompt("Nome do modelo (ex.: Convite para vaga)", "Convite para vaga");
    if (!nome) return;
    const destagar = (t) => (t || "")
      .split(primeiro || "\u0000").join("{nome}")
      .split(vaga?.titulo || "\u0000").join("{vaga}")
      .split(empresaNome).join("{empresa}");
    setSalvando(true); setErro(null);
    try {
      await criarModeloEncerramento({ tipo: 0, nome: nome.trim().slice(0, 120), corpo: destagar(msg), corpoWhatsapp: destagar(msgWpp), padrao: false });
      await recarregarModelos();
      setOk("Modelo salvo ✓ — já aparece na lista para os próximos.");
    } catch (e) { setErro(e.message); } finally { setSalvando(false); }
  };

  const enviar = async () => {
    if (!msg.trim() && !msgWpp.trim()) { setErro("Escreva a mensagem."); return; }
    if (!porEmail && !porWhatsapp) { setErro("Escolha ao menos um canal."); return; }
    setEnviando(true); setErro(null);
    try {
      const r = await abordarCandidatos(vaga.id, { usuarioIds: [c.usuarioId], mensagem: msg.trim(), mensagemWhatsapp: msgWpp.trim(), porEmail, porWhatsapp });
      if (r?.enviados > 0) { setOk("Convite enviado ✓ — você recebe uma cópia por e-mail."); setTimeout(onPronto, 1500); }
      else { setErro("Não foi enviado: o candidato pode estar marcado como não buscando."); setEnviando(false); }
    } catch (e) { setErro(e.message); setEnviando(false); }
  };

  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold mb-2">
        Convidar para a vaga {vaga?.titulo ? `\u201C${vaga.titulo}\u201D` : ""}
      </div>
      {ok && <p role="status" className="text-green-700 text-sm mb-2">{ok}</p>}
      {erro && <p role="alert" className="text-red-600 text-sm mb-2">{erro}</p>}
      {c.jaAbordado && !ok && <p className="text-xs text-blue-700 mb-2">Esta pessoa já foi convidada para esta vaga — evite repetir.</p>}
      {c.jaSeCandidatou && !ok && (
        <p className="text-xs text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-2">
          <b>Esta pessoa já se candidatou a esta vaga.</b> Ela está aguardando o seu retorno — convidar de novo
          soa como se ninguém tivesse visto a candidatura dela. Responda pela aba <b>Candidaturas</b> da vaga.
        </p>
      )}

      <div className="flex flex-wrap gap-2 items-center mb-2">
        {modelos.length > 0 && (
          <select defaultValue="" onChange={(e) => aplicarModelo(e.target.value)}
            className="rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm text-gray-700">
            <option value="">📄 Usar modelo…</option>
            {modelos.map((m) => <option key={m.id} value={m.id}>{m.nome}</option>)}
          </select>
        )}
        <button type="button" onClick={salvarComoModelo} disabled={salvando}
          className="text-xs font-semibold text-primary hover:underline disabled:opacity-50">
          {salvando ? "Salvando…" : "💾 Salvar esta mensagem como modelo"}
        </button>
        {/* Bloqueado para quem já se candidatou: o aviso acima explica o porquê, e desabilitar evita o convite
            sair no impulso. Não é regra escondida — a pessoa aparece na lista, marcada, com o motivo à vista. */}
        <button onClick={enviar} disabled={enviando || c.jaSeCandidatou}
          className="ml-auto rounded-xl bg-primary hover:bg-brand disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold px-4 py-2">
          {enviando ? "Enviando..." : c.jaSeCandidatou ? "já se candidatou" : "✉️ Enviar convite"}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold mb-1">✉️ Mensagem por e-mail</div>
          <HtmlEditor key={`conv-${rev}`} initialHtml={msg} onChange={setMsg} tags={TAGS_ENCERRAMENTO} />
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold mb-1">📲 Mensagem por WhatsApp</div>
          <WhatsappEditor value={msgWpp} onChange={setMsgWpp} tags={TAGS_ENCERRAMENTO} rows={8} placeholder="Oi, {nome}! ..." />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mt-2">
        <label className="flex items-center gap-1.5 text-xs text-gray-600">
          <input type="checkbox" checked={porEmail} onChange={(e) => setPorEmail(e.target.checked)} /> E-mail
        </label>
        <label className="flex items-center gap-1.5 text-xs text-gray-600">
          <input type="checkbox" checked={porWhatsapp} onChange={(e) => setPorWhatsapp(e.target.checked)} /> WhatsApp
        </label>
      </div>
      <p className="text-[11px] text-gray-400 mt-1">O convite sai pelo eaiRH — o candidato nunca vê seu e-mail nem seu telefone, e o retorno continua rastreado aqui.</p>
    </div>
  );
}

// Candidatos compatíveis com a vaga escolhida no combobox — MESMA tabela da página da vaga, para o recrutador
// não aprender duas telas diferentes para a mesma tarefa. Clicar no nome abre o perfil e o convite.
const selClsCand = "rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm outline-none focus:ring-2 focus:ring-primary";
function CandidatosDaVaga({ vagaId, vaga, ids = "" }) {
  const [lista, setLista] = useState(null);
  const [erro, setErro] = useState(null);
  const [aberto, setAberto] = useState(null);
  // Link do resumo diário à empresa (?candidatos=): abre SÓ os candidatos daquele e-mail, e sem o piso de
  // 20% — o e-mail já contou essas pessoas, e um filtro escondendo alguma faria o link desmentir o e-mail.
  // "Ver todos os compatíveis" volta à lista normal da vaga.
  const [soResumo, setSoResumo] = useState(!!ids);
  // Escolher a vaga no combobox trazia 27 pessoas sem nenhum filtro — a empresa parceira reclamou disso em
  // 19/08, com razão: no fim da lista apareciam perfis de outra área. A API já aceitava filtro; a tela é que
  // não passava. O piso de 20% vem preenchido, mas é campo visível: quem quiser ver tudo é só apagar.
  const [filtros, setFiltros] = useState({ area: "", senioridade: "", cidade: "", skill: "", matchMin: ids ? "" : "20", disponibilidade: "", modalidade: "", contratacao: "", pretensaoMax: "" });
  // "CLT 8.000 · PJ 12.000" — os dois quando informados, porque o regime muda o número e o recrutador compara.
  const pretensao = (c) => {
    const fmt = (v) => Number(v).toLocaleString("pt-BR", { maximumFractionDigits: 0 });
    const partes = [c.pretensaoClt > 0 && `CLT ${fmt(c.pretensaoClt)}`, c.pretensaoPj > 0 && `PJ ${fmt(c.pretensaoPj)}`].filter(Boolean);
    return partes.length ? partes.join(" · ") : <span className="text-gray-300">não informou</span>;
  };

  const recarregar = useCallback(() => {
    setLista(null); setErro(null);
    candidatosDaVaga(vagaId, soResumo ? { ...filtros, ids } : filtros).then(setLista).catch((e) => setErro(e.message));
  }, [vagaId, filtros, soResumo, ids]);
  useEffect(() => { recarregar(); }, [recarregar]);
  const aplicar = (n) => setFiltros((f) => ({ ...f, ...n }));

  const cor = (m) => m >= 70 ? "bg-green-100 text-green-800" : m >= 40 ? "bg-yellow-100 text-yellow-800" : "bg-gray-100 text-gray-600";
  const dispCor = (d) => d === "Disponível" ? "text-green-700" : d === "Aberto a propostas" ? "text-blue-700" : "text-gray-500";

  if (erro) return <p className="text-red-600 text-sm">{erro}</p>;
  if (!lista) return <p className="text-gray-500 text-sm">Buscando candidatos...</p>;
  if (lista.length === 0) return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6 text-sm text-gray-600">
      Ninguém compatível ainda para esta vaga. Conforme novos currículos entram, eles aparecem aqui automaticamente.
    </div>
  );

  return (
    <>
    {soResumo && (
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm text-blue-900">
        <span>Mostrando {lista.length === 1 ? "o candidato" : `os ${lista.length} candidatos`} do resumo que você recebeu por e-mail.</span>
        <button type="button" onClick={() => { setSoResumo(false); aplicar({ matchMin: "20" }); }} className="font-semibold underline">
          Ver todos os compatíveis
        </button>
      </div>
    )}
    <div className="mb-3">
    <FiltrosColapsaveis
      onLimpar={() => setFiltros({ area: "", senioridade: "", cidade: "", skill: "", matchMin: "", disponibilidade: "", modalidade: "", contratacao: "", pretensaoMax: "" })}
      resumo={[
        filtros.area && filtros.area,
        filtros.senioridade && filtros.senioridade,
        filtros.cidade && filtros.cidade,
        filtros.disponibilidade && filtros.disponibilidade,
        filtros.modalidade && `aceita ${filtros.modalidade}`,
        filtros.contratacao && `aceita ${filtros.contratacao}`,
        filtros.pretensaoMax && `pretensão ≤ R$ ${filtros.pretensaoMax}`,
        filtros.matchMin && `match ≥ ${filtros.matchMin}%`,
      ]}>
      {/* Mesmos campos da busca livre, e por bom motivo: cortar filtros porque "a vaga já diz" esconde critério
          em vez de simplificar — o recrutador não sabe o que está sendo aplicado nem consegue abrir a régua
          (aceitar São Paulo numa vaga de Curitiba, por exemplo). Tudo começa VAZIO menos o match mínimo: filtro
          pré-preenchido escondido derrubaria candidato sem ninguém pedir. */}
      <select className={campoFiltro} aria-label="Filtrar por área de atuação" value={filtros.area}
        onChange={(e) => aplicar({ area: e.target.value })}>
        <option value="">Área (todas)</option>{AREAS_ATUACAO.map((a) => <option key={a} value={a}>{a}</option>)}
      </select>
      <select className={campoFiltro} aria-label="Filtrar por senioridade" value={filtros.senioridade}
        onChange={(e) => aplicar({ senioridade: e.target.value })}>
        <option value="">Senioridade (todas)</option>{SENIORIDADES.map((x) => <option key={x} value={x}>{x}</option>)}
      </select>
      <input className={campoFiltro} aria-label="Filtrar por cidade" placeholder="Cidade"
        value={filtros.cidade} onChange={(e) => aplicar({ cidade: e.target.value })} />
      <select className={campoFiltro} aria-label="Filtrar por disponibilidade" value={filtros.disponibilidade}
        onChange={(e) => aplicar({ disponibilidade: e.target.value })}>
        <option value="">Disponibilidade (todas)</option>
        <option value="Disponível">Disponível</option>
        <option value="Aberto a propostas">Aberto a propostas</option>
      </select>
      {/* Rótulo de VERDADE, não placeholder: o campo vem preenchido com 20, e placeholder some quando há valor —
          sobrava um número solto que ninguém sabia o que era. */}
      <CampoRotulado rotulo="Match mín." sufixo="%">
        <input type="number" min="0" max="100" step="5" className={campoFiltro} aria-label="Match mínimo (%)"
          value={filtros.matchMin} onChange={(e) => aplicar({ matchMin: e.target.value })} />
      </CampoRotulado>
      <select className={campoFiltro} aria-label="Filtrar por modalidade aceita" value={filtros.modalidade}
        onChange={(e) => aplicar({ modalidade: e.target.value })}>
        <option value="">Modalidade (todas)</option>
        <option value="Remoto">Aceita remoto</option>
        <option value="Hibrido">Aceita híbrido</option>
        <option value="Presencial">Aceita presencial</option>
      </select>
      <select className={campoFiltro} aria-label="Filtrar por regime de contratação aceito" value={filtros.contratacao}
        onChange={(e) => aplicar({ contratacao: e.target.value })}>
        <option value="">Regime (todos)</option>
        <option value="CLT">Aceita CLT</option>
        <option value="PJ">Aceita PJ</option>
      </select>
      {/* Teto, não faixa: o recrutador tem um orçamento, não um intervalo. Vale a MENOR pretensão informada —
          se um dos regimes cabe, a conversa existe. Quem não informou pretensão continua na lista. */}
      <CampoRotulado rotulo="Pretensão até R$">
        <input type="number" min="0" step="500" className={campoFiltro} aria-label="Pretensão máxima (R$)"
          value={filtros.pretensaoMax} onChange={(e) => aplicar({ pretensaoMax: e.target.value })} placeholder="sem teto" />
      </CampoRotulado>
    </FiltrosColapsaveis>
    </div>
    <p className="text-sm text-gray-600 mb-2">{lista.length} candidato(s), do mais compatível para o menos. Toque no nome para ver o perfil e convidar.</p>

    {/* Cartões no celular. Oito colunas numa tela de 6cm viram rolagem lateral, e o recrutador perde justamente
        as colunas do fim — pretensão e o que a pessoa aceita, que é o que decide se vale a conversa. */}
    <div className="space-y-2 md:hidden">
      {lista.map((c) => (
        <div key={c.usuarioId} className="rounded-2xl border border-gray-200 bg-white p-3 shadow-sm">
          <div className="flex items-start gap-2">
            <span className={`font-bold rounded-full px-2.5 py-1 text-xs shrink-0 ${cor(c.match)}`}>{c.match}%</span>
            <div className="min-w-0 flex-1">
              <button onClick={() => setAberto(c)} className="font-semibold text-primary hover:underline text-left">{c.nome}</button>
              {c.temCurriculo && <span className="ml-1 text-xs text-gray-400" title="tem currículo anexado">📎</span>}
              {c.headline && <div className="text-xs text-gray-500">{c.headline}</div>}
              <div className="mt-1 flex flex-wrap gap-1">
                {c.jaAbordado && <span className="text-[10px] rounded-full bg-blue-50 text-blue-700 px-1.5 py-0.5 font-semibold">já convidado</span>}
                {c.jaSeCandidatou && <span className="text-[10px] rounded-full bg-green-100 text-green-800 px-1.5 py-0.5 font-semibold">já se candidatou</span>}
                <span className={`text-[10px] font-semibold ${dispCor(c.disponibilidade)}`}>{c.disponibilidade}</span>
              </div>
            </div>
          </div>
          <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-gray-600">
            <div><dt className="text-gray-400">Senioridade</dt><dd>{c.senioridade || "—"}</dd></div>
            <div><dt className="text-gray-400">Cidade</dt><dd>{c.cidade || "—"}</dd></div>
            <div><dt className="text-gray-400">Aceita</dt><dd>{[rotuloLista(c.modalidades), rotuloLista(c.contratacoes)].filter(Boolean).join(" · ") || "não informou"}</dd></div>
            <div><dt className="text-gray-400">Pretensão</dt><dd>{pretensao(c)}</dd></div>
          </dl>
          {c.skills.length > 0 && (
            <p className="mt-2 text-xs text-gray-500">{c.skills.slice(0, 4).join(", ")}{c.skills.length > 4 ? "…" : ""}</p>
          )}
        </div>
      ))}
    </div>

    <div className="hidden md:block rounded-2xl border border-gray-200 bg-white shadow-sm overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
          <tr>
            <th className="px-4 py-3 text-left">Match</th>
            <th className="px-4 py-3 text-left">Nome</th>
            <th className="px-4 py-3 text-left">Senioridade</th>
            <th className="px-4 py-3 text-left">Cidade</th>
            <th className="px-4 py-3 text-left">Disponib.</th>
            <th className="px-4 py-3 text-left">Aceita</th>
            <th className="px-4 py-3 text-left">Pretensão</th>
            <th className="px-4 py-3 text-left">Skills</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {lista.map((c) => (
            <tr key={c.usuarioId} className="hover:bg-gray-50">
              <td className="px-4 py-3"><span className={`font-bold rounded-full px-2.5 py-1 text-xs ${cor(c.match)}`}>{c.match}%</span></td>
              <td className="px-4 py-3">
                <button onClick={() => setAberto(c)} className="font-semibold text-primary hover:underline text-left">{c.nome}</button>
                {c.temCurriculo && <span className="ml-1 text-xs text-gray-400" title="tem currículo anexado">📎</span>}
                {c.jaAbordado && <span className="ml-1.5 text-[10px] rounded-full bg-blue-50 text-blue-700 px-1.5 py-0.5 font-semibold">já convidado</span>}
                {/* Interesse comprovado: a pessoa se inscreveu sozinha. Fica em VERDE e em destaque — é o
                    candidato mais quente da lista, e é quem está esperando resposta. */}
                {c.jaSeCandidatou && <span className="ml-1.5 text-[10px] rounded-full bg-green-100 text-green-800 px-1.5 py-0.5 font-semibold">já se candidatou</span>}
                {c.headline && <div className="text-xs font-normal text-gray-500">{c.headline}</div>}
              </td>
              <td className="px-4 py-3 text-gray-600">{c.senioridade || "—"}</td>
              <td className="px-4 py-3 text-gray-600">{c.cidade || "—"}</td>
              <td className={`px-4 py-3 text-xs font-semibold ${dispCor(c.disponibilidade)}`}>{c.disponibilidade}</td>
              {/* Filtro sem o valor à vista vira caixa-preta: o recrutador filtra, a lista encolhe e ele não
                  sabe por quê. Mostrar o que a pessoa aceita e quanto pede é o que torna o corte auditável. */}
              <td className="px-4 py-3 text-xs text-gray-600">
                {[rotuloLista(c.modalidades), rotuloLista(c.contratacoes)].filter(Boolean).join(" · ") || <span className="text-gray-300">não informou</span>}
              </td>
              <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{pretensao(c)}</td>
              <td className="px-4 py-3 text-gray-600">{c.skills.slice(0, 4).join(", ")}{c.skills.length > 4 ? "…" : ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {aberto && (
        <ModalCandidato usuarioId={aberto.usuarioId} nome={aberto.nome} headline={aberto.headline}
          match={aberto.match} motivos={aberto.motivos} jaAbordado={aberto.jaAbordado} vaga={vaga}
          onFechar={() => setAberto(null)}
          rodape={<AbordarCandidato vaga={vaga} c={aberto} onPronto={() => { setAberto(null); recarregar(); }} />} />
      )}
    </div>
    </>
  );
}

function TabBtn({ active, onClick, children }) {
  return (
    <button onClick={onClick} type="button" aria-pressed={active}
      className={`text-sm font-semibold rounded-full px-4 py-1.5 ${active ? "bg-brand text-white" : "bg-white border border-gray-300 text-gray-600 hover:bg-gray-50"}`}>
      {children}
    </button>
  );
}
